import type { HelpSection } from './helpContent.types'

export const programManagerHelpContent: HelpSection[] = [
  {
    title: 'Creating Content Modules',
    subSections: [
      {
        entries: [
          {
            question: 'Where do I create a Content Module?',
            answer:
              'Go to the Content Library and select New Module. In a channel, this opens the simplified Content Module workflow.',
          },
          {
            question: 'What is required to save a Content Module?',
            answer: 'A Primary File is required. The module cannot be saved without one.',
          },
          {
            question: 'Can I edit the generated title and description?',
            answer: 'Yes. Program Managers can edit the auto-generated title and description.',
          },
          {
            question: 'Which file types can I use?',
            answer:
              'You can use png, jpg, tiff, mp4, mov, mp3, wav, m4a, txt, md, pdf, pptx, docx, csv and xlsx, or enter content manually.',
          },
          {
            question: 'Are FAQs part of the Content Module creation workflow?',
            answer: 'No. FAQs are removed from the simplified workflow.',
          },
        ],
      },
    ],
  },
  {
    title: 'Free and Subscriber Access',
    subSections: [
      {
        entries: [
          {
            question: 'Where do I set Free or Subscriber access?',
            answer:
              'Set Free or Subscriber access in the Portal Content Library for Program Managers. It is not set inside the Content Module creation workflow.',
          },
          {
            question: 'What can guests do with Free Content Modules?',
            answer: 'Guests can open and consume the primary file.',
          },
          {
            question: 'What can guests do with Subscriber Content Modules?',
            answer:
              'Guests can see the module in the Content Library, but it is locked. Selecting it prompts them to log in or request access.',
          },
          {
            question: 'Can guests view supplementary files?',
            answer: 'No. Guests can only view the primary file for Free Content Modules.',
          },
          {
            question: 'Does Subscriber access limit authenticated End Users?',
            answer:
              'No. Authenticated End Users with channel access can access the full Content Module, including the primary file and supplementary files.',
          },
        ],
      },
    ],
  },
  {
    title: 'Settings',
    subSections: [
      {
        entries: [
          {
            question: 'Where do I edit the Guest Welcome Message?',
            answer:
              'Go to Settings, then select the Welcome Message tab. The Guest Welcome Message is editable by Program Managers.',
          },
          {
            question: 'What is the Subscriber Welcome Message?',
            answer:
              'It is the welcome message authenticated End Users receive from their Personal Sidekick when they first log in.',
          },
          {
            question: 'Where do I add the Request Access link?',
            answer:
              'Go to Settings, then select Access. With Invite only selected, add the URL in the Request Access Link field.',
          },
          {
            question: 'Does Request Access invite the user automatically?',
            answer:
              'No. After the user subscribes or submits the request access form, the Program Manager must invite them through People.',
          },
          {
            question: 'Do Topics affect the guest Content Library?',
            answer:
              'Yes. If Topics are enabled, they can appear as a filter in the Content Library. If Topics are disabled, the Topic filter is hidden.',
          },
        ],
      },
    ],
  },
  {
    title: 'Publishing, Review, and Prioritization',
    subSections: [
      {
        entries: [
          {
            question: 'Does saving publish a Content Module?',
            answer:
              'No. Saving creates or updates the module. The module must be published before End Users can access it.',
          },
          {
            question: 'Can I test a Content Module before publishing it?',
            answer: 'Yes. Use Test in App, then switch to End User - Test in the mobile app.',
          },
          {
            question: 'Who can publish a Content Module?',
            answer:
              'Program Managers can publish their own Content Modules and Expert-created Content Modules that are in Pending status. Experts can publish their own Content Modules only when they have Bypass Review permission.',
          },
          {
            question: 'What is Bypass Review?',
            answer:
              'Bypass Review is a permission that allows an Expert owner to publish their own Content Modules without Program Manager approval. Program Managers manage this permission from the Content Module owner settings in the Content Library.',
          },
          {
            question: 'Does Module Prioritization affect guest Content Library order?',
            answer:
              'Yes. Module Prioritization affects both guest Content Library order and authenticated suggestions.',
          },
        ],
      },
    ],
  },
  {
    title: 'Metrics',
    subSections: [
      {
        entries: [
          {
            question: 'What are Metrics in the Sidekick Platform?',
            answer:
              'Metrics track how End Users engage with content and Sidekicks, helping Program Managers measure effectiveness.',
          },
          {
            question: 'What kinds of data can I see?',
            answer:
              'You can view statistics such as module usage, frequency of Sidekick interactions, and which topics are most accessed.',
          },
          {
            question: 'Where do I find Metrics?',
            answer: 'In the Portal, go to the Metrics tab to review engagement and usage reports.',
          },
          {
            question: 'How are Metrics useful?',
            answer:
              'They show which content is most impactful, help identify gaps, and provide insight into user behavior.',
          },
        ],
      },
    ],
  },
  {
    title: 'People and Trial Accounts',
    subSections: [
      {
        entries: [
          {
            question: 'Do guests appear in People?',
            answer: 'No. Guests are not listed in People unless they are invited as End Users.',
          },
          {
            question: 'When should I invite a user?',
            answer:
              'Invite a user when they should log in, interact with Sidekicks, submit feedback, access Subscriber content, or receive a Trial Account.',
          },
          {
            question: 'Does completing a subscription or request access form automatically add the user?',
            answer:
              'No. After the external process is complete, the Program Manager must invite the user through People.',
          },
          {
            question: 'What is a Trial Account?',
            answer:
              'A Trial Account gives an End User full channel access for one month after they accept the trial invitation.',
          },
          {
            question: 'How do I invite an End User to a one-month free trial?',
            answer:
              "Open People, select the End Users tab, select Invite End User, enter the user's email address and name, select the One-month free trial checkbox, preview the email, and then send the invitation.",
          },
          {
            question: 'Is the one-month free trial selected by default?',
            answer:
              'No. The Program Manager must actively select the One-month free trial checkbox before sending the invitation.',
          },
          {
            question: 'Should I preview the email before sending a trial invitation?',
            answer: 'Yes. Use Preview Email to confirm the invitation email matches the free trial selection.',
          },
          {
            question: 'What Trial Account statuses and actions should I know?',
            answer:
              "Trial-related actions depend on the End User's current status.\n\n| Status | What it means | Common actions |\n| --- | --- | --- |\n| Invited | The End User has been invited to a Trial Account but has not accepted the invitation yet. | Edit, Remove, Upgrade Account |\n| Invitation Expired | The trial invitation expired before the End User accepted it. | Edit, Resend Invite, Remove |\n| Trial | The End User accepted the trial invitation and is currently in the one-month trial period. | Edit, Remove, Upgrade Account |\n| Trial Expired | The one-month trial period ended. | Edit, Remove, Upgrade Account |",
          },
          {
            question: 'What does Upgrade Account do?',
            answer: 'Upgrade Account converts an eligible Trial Account to a full account.',
          },
          {
            question: 'When can I upgrade a Trial Account?',
            answer:
              "Upgrade Account is available for invited trial users, active Trial Accounts, and expired Trial Accounts when the action appears in the End User's account actions menu.",
          },
          {
            question: 'Can I remove a Trial Account?',
            answer: 'Yes. Use Remove when the End User should no longer have access to the channel.',
          },
        ],
      },
    ],
  },
  {
    title: 'Feedback',
    subSections: [
      {
        entries: [
          {
            question: 'Can guest users submit feedback?',
            answer: 'No. Guests cannot submit feedback. Feedback is available for authenticated End Users.',
          },
          {
            question: 'Can Trial Account users submit feedback?',
            answer:
              'Yes. Trial Account users are authenticated End Users during their trial period, so they can submit feedback while they have access.',
          },
        ],
      },
    ],
  },
]
